/**
 * Cable profiles -- group 10.
 *
 * Real Cable, Norstone, Esprit Audio, Absolue Creations, Odeion Cables,
 * Signal Projects, Titan Audio, Russ Andrews.
 *
 * Six of the eight are French or British boutiques that publish construction
 * descriptions and prices but no resistance, inductance or capacitance figures.
 * None are passed here. Every entry lets the kit derive R from gauge and
 * material and L and C from geometry, and each provenance note says which of
 * gauge, material and geometry came from the maker and which was inferred from
 * the class of cable.
 *
 * Several of these makers name products in French. Accented characters are
 * stripped for the ASCII rule of this database, so "Sigma cable d'enceintes"
 * appears without its circumflex and "Numerique" without its acute accent.
 *
 * Notes describe construction and, for the two types the engine actually
 * models, the electrical consequence. Power, USB and Ethernet entries describe
 * the build only. Several of the cords below are serious pieces of metalwork
 * that the DSP does nothing at all with, and saying so is the point.
 */

import type { CableProfile } from '../../types/hardware';
import { makeCable } from '../cableKit';
import { usd } from '../kit';

export const CABLES_10: CableProfile[] = [
  // =========================================================================
  // Real Cable -- Guillerval, France. Broad catalogue from bulk OFC up to the
  // chateau-named MASTER range.
  // =========================================================================

  makeCable({
    mfr: 'Real Cable',
    model: 'CHEVERNY SP',
    cableType: 'speaker',
    price: usd(215, 'APPROXIMATE', 2024, 'About EUR 200 for a 2 x 3 m terminated pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'ofc',
    geometry: 'air-tube',
    gauge: 12,
    dielectric: 'Cotton, with air filling the tube volume',
    termination: ['banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0392b',
    tags: ['master-range', 'hollow-tube', 'cotton-dielectric'],
    notes:
      'Four hollow tubular OFC conductors held apart inside a 14.5 mm jacket, insulated with cotton so most of the dielectric volume is air. Wide spacing is what the tube construction buys: shunt capacitance falls well below a close-spaced zip cord and loop inductance rises in exchange, which is the trade every cable geometry makes. Into a loudspeaker it is the inductance term that matters, and at this gauge over 3 m it stays small next to the driver impedance.',
    provenanceNote:
      'Conductor material, hollow-tube construction, cotton dielectric and 14.5 mm outer diameter are from the maker\'s published description. Gauge is inferred from that diameter and the four-tube layout rather than stated. Electrical figures are derived from gauge, material and geometry.',
  }),

  makeCable({
    mfr: 'Real Cable',
    model: 'CHAMBORD SP II',
    cableType: 'speaker',
    price: usd(540, 'APPROXIMATE', 2024, 'About EUR 500 for a 2 x 3 m terminated pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 11,
    termination: ['banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a93226',
    tags: ['master-range', 'hollow-tube', 'ohno-continuous-cast'],
    notes:
      'The same hollow-tube layout as the Cheverny with Ohno continuous-cast copper in place of ordinary OFC. OCC is drawn as a single long grain rather than a stack of crystallites; its bulk resistivity is within about one percent of OFC, so the gauge and the tube spacing, not the casting process, set what the engine computes. Heavier section than the Cheverny, so lower series resistance over the same run.',
    provenanceNote:
      'Conductor material and hollow-tube construction are from the maker\'s published description. Gauge is inferred from the cable class. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Real Cable',
    model: 'ELITE 500',
    cableType: 'speaker',
    price: usd(95, 'APPROXIMATE', 2024, 'About EUR 88 for a 2 x 3 m pair from bulk cable; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 11,
    termination: ['banana', 'spade', 'bare'],
    confidence: 'ESTIMATED',
    accent: '#d05c4f',
    tags: ['bulk-cable', 'value'],
    notes:
      'Conventional heavy-section multistrand OFC in a bonded two-conductor jacket. Two conductors run side by side and close together, which gives low loop inductance and correspondingly high capacitance -- the right way round for a speaker cable, where the amplifier sees the inductance and the capacitance is shunted by a few ohms of driver.',
    provenanceNote:
      'Conductor material and the position of this reference in the range are published; the flat bonded geometry and the gauge are inferred from the cable class rather than stated per model. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Real Cable',
    model: 'SPI-VIM',
    cableType: 'speaker',
    price: usd(45, 'APPROXIMATE', 2024, 'About EUR 42 for a 2 x 3 m pair from bulk cable; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 14,
    termination: ['bare', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#e07a70',
    tags: ['bulk-cable', 'entry'],
    notes:
      'Light-section bulk OFC sold by the metre. At 14 AWG the loop resistance is roughly 0.017 ohm per metre, so a 3 m run adds about 0.05 ohm in series with the loudspeaker. Against a nominal 8 ohm load that is under 0.06 dB; against a 3 ohm dip it is nearer 0.15 dB, and the engine reports whichever the chosen speaker actually presents.',
    provenanceNote:
      'Conductor material is published; geometry and gauge are inferred from the cable class. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Real Cable',
    model: 'CHEVERNY II-RCA',
    cableType: 'interconnect',
    price: usd(325, 'APPROXIMATE', 2024, 'About EUR 300 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided copper screen',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0392b',
    tags: ['master-range', 'single-ended'],
    notes:
      'Single-ended coaxial construction with the return doubling as the screen, which is the standard arrangement for an RCA lead and the reason its capacitance is higher than a shielded twisted pair of the same gauge. At coax-typical capacitance a 1 m run loads a source with well under 100 pF; against a 100 ohm solid-state output that puts the corner in the tens of megahertz, and the engine reports the in-band result, which rounds to nothing.',
    provenanceNote:
      'Range placement, connector type and screened coaxial construction are published. Gauge is inferred from the cable class. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Real Cable',
    model: 'CHEVERNY II-XLR',
    cableType: 'interconnect',
    price: usd(380, 'APPROXIMATE', 2024, 'About EUR 350 for a 1 m balanced pair; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided copper screen over the twisted pair',
    termination: ['XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c0392b',
    tags: ['master-range', 'balanced'],
    notes:
      'The balanced version of the Cheverny lead: a screened twisted pair rather than a coax, so the signal and its inverse sit symmetrically inside the screen. Twisting the pair puts both conductors in the same interference field, which is what lets the receiving differential stage subtract common-mode pickup. Pair capacitance is lower than the coaxial RCA of the same gauge, and both are far below the level at which anything happens in the audio band.',
    provenanceNote:
      'Range placement, connector type and screened balanced construction are published. Gauge is inferred from the cable class. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Real Cable',
    model: 'CHAMBORD Secteur',
    cableType: 'power',
    price: usd(540, 'APPROXIMATE', 2024, 'About EUR 500 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'France',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Screened',
    termination: ['IEC C15', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#8e2f26',
    tags: ['master-range', 'shielded'],
    notes:
      'The MASTER-range mains cord: heavy OCC copper conductors, a screen, and moulded Schuko and IEC ends rated for European 16 A service. Gauge and rating are the facts that matter for a power amplifier drawing large transient current through a long run. The engine applies no signal processing to power cords, so choosing this changes the chain description and the system price and leaves the audio bit-identical.',
    provenanceNote:
      'Conductor material and range placement are published. Gauge, current rating and geometry are inferred from the class of cord and the European market it is sold into. No electrical figures are published and none are used in the signal path.',
  }),

  makeCable({
    mfr: 'Real Cable',
    model: 'PSKAP 15',
    cableType: 'power',
    price: usd(150, 'APPROXIMATE', 2024, 'About EUR 140 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 16,
    shielding: 'Double shield',
    dielectric: 'Kapton (polyimide)',
    termination: ['IEC C13', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b0463a',
    tags: ['kapton', 'double-shielded'],
    notes:
      'OFC conductors insulated with Kapton polyimide under a double shield. Kapton is a genuinely unusual choice for a mains cord -- it is a high-temperature aerospace film, chosen here for its dielectric strength and thermal rating rather than for anything to do with audio. Real engineering, and the engine applies zero DSP to it.',
  }),

  makeCable({
    mfr: 'Real Cable',
    model: 'iPlug-USB60',
    cableType: 'usb',
    price: usd(35, 'APPROXIMATE', 2024, 'About EUR 32 for a 1 m assembly; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil and braid',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#d05c4f',
    tags: ['usb-2.0', 'entry'],
    notes:
      'A short compliant USB 2.0 assembly with a 90 ohm differential pair, foil and braid screen, and separate power conductors. The data pair either meets the eye-diagram requirement of the standard or the link fails; there is no intermediate state for it to colour. The engine applies no signal processing here.',
    provenanceNote:
      'Cable type and reference are from the maker\'s catalogue. Gauge, screening and the 90 ohm differential impedance follow from the USB 2.0 specification any compliant assembly must meet, and are not published per model.',
  }),

  makeCable({
    mfr: 'Real Cable',
    model: 'UNIVERS',
    cableType: 'usb',
    price: usd(60, 'APPROXIMATE', 2024, 'About EUR 55 for a 1.5 m assembly; converted.'),
    priceLengthM: 1.5,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil and braid',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#c0392b',
    tags: ['usb-2.0'],
    notes:
      'Heavier 24 AWG power conductors than the entry assembly, which reduces the volt drop on the 5 V bus over a longer run -- relevant only to a bus-powered device, since a mains-powered DAC ignores that rail entirely. The differential pair meets the same 90 ohm requirement either way. No signal processing is applied.',
    provenanceNote:
      'Cable type and reference are from the maker\'s catalogue. Gauge and screening follow from the USB 2.0 specification rather than a published per-model figure.',
  }),

  makeCable({
    mfr: 'Real Cable',
    model: 'E-NET 600-2',
    cableType: 'ethernet',
    price: usd(50, 'APPROXIMATE', 2024, 'About EUR 46 for a 2 m patch lead; converted.'),
    priceLengthM: 2,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: 'Cat 6, 250 MHz',
    shielding: 'F/UTP, overall foil',
    termination: ['RJ45'],
    confidence: 'ESTIMATED',
    accent: '#d05c4f',
    tags: ['cat6', 'shielded'],
    notes:
      'Four 100 ohm twisted pairs with an overall foil screen, terminated in shielded RJ45. Cat 6 rates the assembly to 250 MHz, which is a crosstalk and return-loss specification, not an audio one. Ethernet frames are checksummed and retransmitted on error and a streamer buffers seconds ahead of playback, so no analog path exists between the ends. The engine applies no signal processing.',
    provenanceNote:
      'Reference and category rating are from the maker\'s catalogue. Gauge, pair count and 100 ohm impedance follow from the Cat 6 standard rather than a published per-model figure.',
  }),

  makeCable({
    mfr: 'Real Cable',
    model: 'EIGHT STREAM',
    cableType: 'ethernet',
    price: usd(110, 'APPROXIMATE', 2024, 'About EUR 100 for a 2 m patch lead; converted.'),
    priceLengthM: 2,
    country: 'France',
    conductor: 'ofc',
    geometry: 'star-quad',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: 'Cat 7, 600 MHz',
    shielding: 'S/FTP, individually foiled pairs in an overall braid',
    termination: ['RJ45'],
    confidence: 'ESTIMATED',
    accent: '#a93226',
    tags: ['cat7', 'shielded'],
    notes:
      'Individually foiled pairs inside an overall braid, rated to 600 MHz. Screening each pair separately is what suppresses pair-to-pair crosstalk at those frequencies; it is a data-integrity measure and does nothing downstream of a buffer. Listed here because the build is real and the DSP contribution is exactly zero.',
    provenanceNote:
      'Reference and category rating are from the maker\'s catalogue. Gauge, screening arrangement and impedance follow from the Cat 7 standard rather than a published per-model figure.',
  }),

  // =========================================================================
  // Norstone -- France. Three colour-coded ranges, Arran, Skye and Jura, plus
  // terminated MC speaker leads and bulk cable by the metre.
  // =========================================================================

  makeCable({
    mfr: 'Norstone',
    model: 'Arran MC',
    cableType: 'speaker',
    price: usd(95, 'APPROXIMATE', 2024, 'About EUR 89 for a terminated 3 m pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'ofc',
    geometry: 'flat-parallel',
    gauge: 14,
    termination: ['banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2d7dd2',
    tags: ['entry-range', 'terminated', 'gold-plated'],
    notes:
      'The entry terminated lead: 99.99 percent OFC in a bonded pair with 24 carat gold-plated bananas. Gold plating on a banana matters for contact corrosion over years, not for conductivity -- gold is a worse conductor than the copper underneath it, and the plating is microns thick. What the engine sees is the 14 AWG loop resistance and the close-spaced geometry.',
    provenanceNote:
      'Conductor purity, connector plating and terminated construction are published. Gauge and the bonded flat geometry are inferred from the cable class. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Norstone',
    model: 'Skye MC',
    cableType: 'speaker',
    price: usd(160, 'APPROXIMATE', 2024, 'About EUR 149 for a terminated 3 m pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'silver-plated-copper',
    geometry: 'star-quad',
    gauge: 13,
    termination: ['banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3a8dd8',
    tags: ['mid-range', 'terminated', 'four-conductor'],
    notes:
      'Four individually insulated OFC conductors, tin and one percent silver plated, paralleled in opposing pairs and terminated in gold-plated bananas. The four-conductor arrangement is the same idea as pro-audio star quad: opposing pairs cancel much of the external field, which lowers loop inductance relative to a simple two-conductor lead at the same section. The plating is a surface treatment; the DC path is still the copper core.',
    provenanceNote:
      'Conductor material, plating, four-conductor count and connector type are published. Gauge and the quad arrangement of those four conductors are inferred. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Norstone',
    model: 'Jura MC',
    cableType: 'speaker',
    price: usd(320, 'APPROXIMATE', 2024, 'About EUR 299 for a terminated 3 m pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 13,
    termination: ['banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#1f5f9e',
    tags: ['flagship-range', 'terminated', 'occ'],
    notes:
      'Norstone quotes 2.5 sq mm of OCC-6N and OFC-4N copper, which is close to 13 AWG per leg. That section is the number the engine uses: about 0.013 ohm of loop resistance per metre, or roughly 0.04 ohm over a 3 m run. Whether that is audible depends entirely on the impedance minimum of the loudspeaker it is driving, which the engine already knows.',
    provenanceNote:
      'Conductor materials, 2.5 sq mm section and connector options are published; gauge here is that section converted to AWG. Geometry is inferred. Electrical figures are derived from gauge and material.',
  }),

  makeCable({
    mfr: 'Norstone',
    model: 'Premium PR250',
    cableType: 'speaker',
    price: usd(75, 'APPROXIMATE', 2024, 'About EUR 70 for a 2 x 3 m pair from bulk cable; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'ofc',
    geometry: 'concentric',
    gauge: 13,
    termination: ['bare', 'banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5aa0e0',
    tags: ['bulk-cable', 'star-structure', 'nineteen-strand'],
    notes:
      'Bulk cable at 2.5 sq mm per leg built as nineteen concentric strands in what Norstone calls a star structure. Concentric lay packs the strands into a round cross-section with each layer wound opposite to the last, which keeps the bundle stable and the geometry repeatable along the run. Electrically it sits between a flat bonded pair and a widely spaced one for both inductance and capacitance.',
    provenanceNote:
      'Conductor material, 2.5 sq mm section and the nineteen-strand concentric construction are published. Electrical figures are derived from that section and geometry.',
  }),

  makeCable({
    mfr: 'Norstone',
    model: 'Signature ST250',
    cableType: 'speaker',
    price: usd(140, 'APPROXIMATE', 2024, 'About EUR 130 for a 2 x 3 m pair from bulk cable; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'occ-copper',
    geometry: 'concentric',
    gauge: 13,
    termination: ['bare', 'banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#1f5f9e',
    tags: ['bulk-cable', 'occ', 'nineteen-strand'],
    notes:
      'The same nineteen-strand concentric build as the PR250 at the same 2.5 sq mm, with OCC-6N and OFC-4N copper instead of ordinary OFC. Same section, same geometry, so the derived resistance, inductance and capacitance are within about one percent of the cheaper cable -- the difference in this listing is the metallurgy and the price, and the engine is not going to pretend otherwise.',
    provenanceNote:
      'Conductor materials, section and concentric construction are published. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Norstone',
    model: 'Arran RCA',
    cableType: 'interconnect',
    price: usd(36, 'CURRENT_RETAIL', 2024, 'About EUR 32.90 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 24,
    shielding: 'Triple shield',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2d7dd2',
    tags: ['entry-range', 'triple-shield'],
    notes:
      'Five-nines OFC under a triple screen with gold-plated aluminium bodies. Three screening layers is more than a domestic interconnect needs for hum rejection and costs almost nothing in capacitance, since the screen spacing rather than the layer count sets it. A 1 m run at coax-typical capacitance is a load of well under 100 pF on the source.',
    provenanceNote:
      'Conductor purity, triple screening and connector construction are published. Gauge is inferred from the cable class. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Norstone',
    model: 'Jura RCA',
    cableType: 'interconnect',
    price: usd(75, 'CURRENT_RETAIL', 2024, 'About EUR 68.90 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'silver-plated-copper',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Triple shield',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#1f5f9e',
    tags: ['flagship-range', 'triple-shield', 'phono-ground'],
    notes:
      'Five-nines OFC plated with ten percent silver, triple screened, and supplied with a separate phono earth lead so it can be used between a turntable and a phono stage. That earth wire is the functional difference from the rest of the range: it gives the arm and chassis a defined path to the preamplifier ground, which is a hum question rather than a signal-quality one.',
    provenanceNote:
      'Conductor material, plating proportion, screening and the included ground wire are published. Gauge is inferred. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Norstone',
    model: 'Jura XLR',
    cableType: 'interconnect',
    price: usd(375, 'CURRENT_RETAIL', 2024, 'About EUR 349 for a 1 m balanced pair; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Triple shield over the twisted pair',
    termination: ['XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#1f5f9e',
    tags: ['flagship-range', 'balanced', 'triple-shield'],
    notes:
      'Balanced version of the Jura: a screened twisted pair of silver-plated five-nines OFC. Capacitance across a twisted pair is lower than across a coax of the same gauge because the two conductors are farther apart than a centre core is from its braid, and a balanced link is driven from a low impedance at both ends anyway, so the loading is even less consequential than on the single-ended lead.',
    provenanceNote:
      'Conductor material, plating and screening are published. Gauge and the twisted-pair geometry are inferred from the balanced format. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Norstone',
    model: 'Arran Power Cable',
    cableType: 'power',
    price: usd(85, 'CURRENT_RETAIL', 2024, 'About EUR 79 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 10,
    termination: ['IEC C13', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#2d7dd2',
    tags: ['entry-range'],
    notes:
      'Entry mains cord in the Arran range: OFC conductors, moulded Schuko plug and IEC socket, rated for ordinary 10 A domestic service. That rating is the whole engineering story and it is enough for any source component. The engine applies no signal processing to power cords.',
    provenanceNote:
      'Range placement and conductor material are published. Gauge, current rating and geometry are inferred from the class of cord and the European market it is sold into. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Norstone',
    model: 'Skye Power Cable',
    cableType: 'power',
    price: usd(215, 'CURRENT_RETAIL', 2024, 'About EUR 199 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'France',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 16,
    shielding: 'Screened',
    termination: ['IEC C13', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#3a8dd8',
    tags: ['mid-range', 'shielded'],
    notes:
      'Heavier section than the Arran cord with silver-plated conductors and a screen, rated for 16 A. Plating a mains conductor with silver has no measurable effect at 50 Hz -- skin depth in copper at that frequency is about 9 mm, so the current is using the whole cross-section, not the surface. The gauge and the connector quality are the real specifications. No DSP is applied.',
    provenanceNote:
      'Range placement and conductor plating are published. Gauge, rating and geometry are inferred from the class of cord. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Norstone',
    model: 'Jura Power Cable',
    cableType: 'power',
    price: usd(430, 'CURRENT_RETAIL', 2024, 'About EUR 399 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'France',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Screened',
    termination: ['IEC C13', 'IEC C15', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#1f5f9e',
    tags: ['flagship-range', 'shielded'],
    notes:
      'The heaviest cord Norstone builds: 12 AWG silver-plated OFC under a screen with moulded 16 A ends. At that section the volt drop over 1.5 m at 5 A is a couple of hundredths of a volt, which is the honest measure of what a mains cord can and cannot do. The engine applies no signal processing to power cords, so this entry changes the chain description and the price and nothing else.',
    provenanceNote:
      'Range placement and conductor plating are published. Gauge, rating and geometry are inferred from the class of cord. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Norstone',
    model: 'Arran Cable USB',
    cableType: 'usb',
    price: usd(32, 'CURRENT_RETAIL', 2024, 'About EUR 29 for a 1.5 m assembly; converted.'),
    priceLengthM: 1.5,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil and braid',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#2d7dd2',
    tags: ['usb-2.0', 'entry'],
    notes:
      'A plain compliant USB 2.0 lead with gold-plated contacts. In an asynchronous USB DAC the conversion clock is generated at the DAC and the host is told when to send, so nothing about the cable enters the timing of the conversion. The engine applies no signal processing here.',
    provenanceNote:
      'Cable type and connector plating are from the maker\'s catalogue. Gauge, screening and impedance follow from the USB 2.0 specification rather than a published per-model figure.',
  }),

  // =========================================================================
  // Esprit Audio -- France. Ten families from Alpha up to L'Esprit, each one
  // offered across speaker, RCA, XLR, digital, USB, RJ45, phono and mains.
  // Prices are published per family; construction detail is not, so almost
  // everything below is marked estimated and says so.
  // =========================================================================

  makeCable({
    mfr: 'Esprit Audio',
    model: 'Alpha RCA G9',
    cableType: 'interconnect',
    price: usd(240, 'CURRENT_RETAIL', 2024, 'About EUR 220 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'occ-copper',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Screened',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#9b8cf0',
    tags: ['entry-family', 'single-ended', 'ninth-generation'],
    notes:
      'The bottom rung of the Esprit range and the only one carrying a generation suffix in the current price list. Single-ended screened construction at interconnect gauge; a 1 m run presents a load of well under 100 pF to the source, which against any normal output impedance leaves the audio band untouched. The engine computes that load and reports what it comes to rather than what it ought to feel like.',
    provenanceNote:
      'Family name, cable type and retail price are from the maker\'s published range. Conductor material, gauge, screening and geometry are inferred from the class of cable; Esprit does not publish construction detail per model. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Esprit Audio',
    model: 'Alpha Secteur',
    cableType: 'power',
    price: usd(380, 'APPROXIMATE', 2024, 'About EUR 350 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'France',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 16,
    shielding: 'Screened',
    termination: ['IEC C13', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#9b8cf0',
    tags: ['entry-family'],
    notes:
      'Hand-assembled mains cord in the entry family, sold with European 16 A ends. Esprit builds a cord for every family in the range, so this is the cheapest way into the line. The engine applies no signal processing to power cords: the cord, its gauge and its price are real and the DSP contribution is exactly zero.',
    provenanceNote:
      'Family, cable type and approximate price are from the maker\'s published range. Gauge, rating, screening and geometry are inferred from the class of cord. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Esprit Audio',
    model: 'Beta HP',
    cableType: 'speaker',
    price: usd(475, 'CURRENT_RETAIL', 2024, 'About EUR 440 for a terminated pair; converted. Length and termination change the price.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 13,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#8f7ce8',
    tags: ['entry-family'],
    notes:
      'Second family up, in speaker form. At 13 AWG the loop resistance works out near 0.013 ohm per metre, so a 3 m run puts roughly 0.04 ohm in series with the loudspeaker -- about 0.04 dB of loss into 8 ohms and three times that into a 3 ohm dip. That number, and its variation with the impedance curve of the chosen speaker, is the entire modelled effect.',
    provenanceNote:
      'Family, cable type and price are published. Conductor material, gauge and geometry are inferred from the class of cable. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Esprit Audio',
    model: 'Kappa RCA',
    cableType: 'interconnect',
    price: usd(520, 'CURRENT_RETAIL', 2024, 'About EUR 480 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'occ-copper',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Screened',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#8f7ce8',
    tags: ['mid-family', 'single-ended'],
    notes:
      'Third family, single-ended. Esprit prices climb steeply through the range while the cable type and length stay the same, which makes this a useful place to watch what the engine does: the derived capacitance for a 1 m screened coax is the same here as it is three families lower, and so is the computed in-band response.',
    provenanceNote:
      'Family, cable type and price are published. Conductor material, gauge, screening and geometry are inferred from the class of cable. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Esprit Audio',
    model: 'Celesta HP',
    cableType: 'speaker',
    price: usd(1040, 'CURRENT_RETAIL', 2024, 'About EUR 960 for a terminated pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#7f5af0',
    tags: ['mid-family'],
    notes:
      'Heavier section than the Beta lead, which is the change that actually shows up in the model: about a quarter less loop resistance per metre, so a little less of the amplifier damping factor is thrown away in the cable. On a short run into a benign load the difference is a few hundredths of a decibel; on a long run into a difficult one it is worth having.',
    provenanceNote:
      'Family, cable type and price are published. Conductor material, gauge and geometry are inferred from the class of cable. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Esprit Audio',
    model: 'Celesta USB',
    cableType: 'usb',
    price: usd(430, 'CURRENT_RETAIL', 2024, 'About EUR 400 for a 1 m A to B assembly; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil and braid',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#7f5af0',
    tags: ['usb-2.0', 'mid-family'],
    notes:
      'Esprit builds an A to B USB lead in most of its families; this is the Celesta version. Whatever the conductor metallurgy, a USB 2.0 link either meets the eye-diagram and impedance requirements of the standard or it drops packets, and an asynchronous DAC clocks conversion locally in either case. The engine applies no signal processing here.',
    provenanceNote:
      'Family and cable type are from the maker\'s published range. Gauge, screening and impedance follow from the USB 2.0 specification rather than a published per-model figure.',
  }),

  makeCable({
    mfr: 'Esprit Audio',
    model: 'Eterna XLR',
    cableType: 'interconnect',
    price: usd(1710, 'CURRENT_RETAIL', 2024, 'About EUR 1580 for a 1 m balanced pair; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Screened twisted pair',
    termination: ['XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#6b46e0',
    tags: ['upper-family', 'balanced'],
    notes:
      'Balanced lead from the family Esprit positions in the middle of its range. A screened twisted pair driven differentially rejects common-mode pickup by subtraction at the receiver, which is a real and measurable property of the format rather than of this particular cable. Derived pair capacitance over 1 m is far too small to matter against any line-stage output impedance.',
    provenanceNote:
      'Family, cable type and price are published. Conductor material, gauge and geometry are inferred from the balanced format and the class of cable. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Esprit Audio',
    model: 'Eterna HP',
    cableType: 'speaker',
    price: usd(1980, 'CURRENT_RETAIL', 2024, 'About EUR 1830 for a terminated pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#6b46e0',
    tags: ['upper-family'],
    notes:
      'Heavier again at around 11 AWG per leg, which brings loop resistance down near 0.008 ohm per metre. Past this point extra copper buys very little: the cable is already an order of magnitude below the voice-coil resistance it feeds, and the amplifier output impedance usually dominates what is left.',
    provenanceNote:
      'Family, cable type and price are published. Conductor material, gauge and geometry are inferred from the class of cable. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Esprit Audio',
    model: 'Lumina HP',
    cableType: 'speaker',
    price: usd(5300, 'CURRENT_RETAIL', 2024, 'About EUR 4910 for a terminated pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'occ-copper',
    geometry: 'litz',
    gauge: 11,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#5b34d6',
    tags: ['upper-family', 'litz'],
    notes:
      'Litz-style construction, meaning many individually insulated strands transposed through the bundle so that no strand stays on the outside of the turn. That arrangement was devised to fight skin effect at radio frequencies; at 20 kHz the skin depth in copper is about 0.47 mm, comparable to the whole strand, so the mechanism has very little left to do in the audio band. The geometry does lower inductance relative to a simple twisted pair, and that part the engine does model.',
    provenanceNote:
      'Family, cable type and price are published. Conductor material, gauge and the litz construction are inferred from the class of cable rather than stated per model. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Esprit Audio',
    model: 'Gaia HP',
    cableType: 'speaker',
    price: usd(19500, 'CURRENT_RETAIL', 2024, 'About EUR 18090 for a terminated pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'occ-copper',
    geometry: 'litz',
    gauge: 10,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#4a2ac0',
    tags: ['flagship-family', 'litz'],
    notes:
      'Second from the top of the range. The section is generous enough that the derived loop resistance over a 3 m run is around 0.02 ohm, roughly a fiftieth of a nominal 8 ohm load. That figure is what the engine puts in series with the loudspeaker, and it will be the same figure for any cable of the same gauge and geometry regardless of what it costs.',
    provenanceNote:
      'Family, cable type and price are published. Conductor material, gauge and geometry are inferred from the class of cable. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Esprit Audio',
    model: 'L\'Esprit RCA',
    cableType: 'interconnect',
    price: usd(28100, 'CURRENT_RETAIL', 2024, 'About EUR 26000 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'occ-copper',
    geometry: 'coaxial',
    gauge: 20,
    shielding: 'Screened',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#3d1fa8',
    tags: ['flagship-family', 'single-ended'],
    notes:
      'The flagship interconnect of the range and the most expensive cable in this group. It is a 1 m screened single-ended lead, and the load it puts on a source is a few tens of picofarads, exactly as with the family a hundredth of the price. The engine reports the computed in-band effect, which for both of them is nil to two decimal places.',
    provenanceNote:
      'Family, cable type and price are published. Conductor material, gauge, screening and geometry are inferred from the class of cable. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Esprit Audio',
    model: 'Eterna RJ45',
    cableType: 'ethernet',
    price: usd(1400, 'APPROXIMATE', 2024, 'About EUR 1300 for a 1.5 m patch lead; converted.'),
    priceLengthM: 1.5,
    country: 'France',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: 'Cat 7 class, 100 ohm balanced pairs',
    shielding: 'Screened pairs in an overall screen',
    termination: ['RJ45'],
    confidence: 'ESTIMATED',
    accent: '#6b46e0',
    tags: ['shielded', 'upper-family'],
    notes:
      'Esprit lists an RJ45 variant in each family; this is the Eterna one. Four 100 ohm balanced pairs, screened, terminated in RJ45. Ethernet frames carry a CRC and are retransmitted when it fails, and a streamer holds seconds of audio in a buffer ahead of the converter, so there is no continuous analog path from one end to the other. The engine applies no signal processing here.',
    provenanceNote:
      'The family and the existence of an RJ45 variant are from the maker\'s published range description; the price is approximate. Gauge, pair impedance and screening follow from the Ethernet standard rather than a published per-model figure.',
  }),

  // =========================================================================
  // Absolue Creations -- France. One construction family, TIM, in ascending
  // series: IN-TIM, ES-TIM, OP-TIM, UL-TIM, with TIM Reference as the former
  // flagship.
  // =========================================================================

  makeCable({
    mfr: 'Absolue Creations',
    model: 'IN-TIM Speaker',
    cableType: 'speaker',
    price: usd(1350, 'APPROXIMATE', 2024, 'About EUR 1250 for a terminated 3 m pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#d4af37',
    tags: ['entry-series', 'tim-family'],
    notes:
      'The entry point into the TIM construction family, sold as a full loom alongside matching interconnects and cords. Stranded copper at roughly 12 AWG per leg, which puts about 0.03 ohm of loop resistance across a 3 m run. What the engine does with that depends on the impedance minimum of the loudspeaker, not on the series name.',
    provenanceNote:
      'Series name and cable type are from the maker\'s published range. Conductor material, gauge and geometry are inferred from the class of cable; Absolue Creations publishes technology names rather than construction figures. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Absolue Creations',
    model: 'IN-TIM Interconnect',
    cableType: 'interconnect',
    price: usd(870, 'APPROXIMATE', 2024, 'About EUR 800 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Screened',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#d4af37',
    tags: ['entry-series', 'tim-family'],
    notes:
      'Entry-series interconnect, offered single-ended and balanced. A 1 m screened lead loads a source with a few tens of picofarads, which against a solid-state output puts the low-pass corner in the megahertz. The engine computes it anyway and reports the in-band figure.',
    provenanceNote:
      'Series name, cable type and termination options are from the maker\'s published range. Conductor material, gauge and geometry are inferred. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Absolue Creations',
    model: 'ES-TIM Speaker',
    cableType: 'speaker',
    price: usd(2700, 'APPROXIMATE', 2024, 'About EUR 2500 for a terminated 3 m pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#c9a227',
    tags: ['mid-series', 'tim-family'],
    notes:
      'The volume seller of the range. Silver-plated stranded copper at around 11 AWG per leg; the plating is an anti-oxidation surface treatment, so the DC path the engine models is the copper core underneath and the resistance follows the section, not the coating.',
    provenanceNote:
      'Series name and cable type are published, and the maker states that its stranded conductors are silver-plated copper. Gauge and geometry are inferred from the class of cable. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Absolue Creations',
    model: 'ES-TIM Interconnect',
    cableType: 'interconnect',
    price: usd(1620, 'APPROXIMATE', 2024, 'About EUR 1500 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'silver-plated-copper',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Screened',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#c9a227',
    tags: ['mid-series', 'tim-family'],
    notes:
      'Mid-series interconnect in single-ended and balanced form. Absolue Creations describes proprietary damping and flux-concentration measures in this line; none of them is a resistance, inductance or capacitance, so none of them reaches the engine. The derived pair figures for a 1 m screened lead are what gets modelled.',
    provenanceNote:
      'Series name, cable type, termination options and the silver-plated copper conductor are from the maker\'s published material. Gauge and geometry are inferred. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Absolue Creations',
    model: 'OP-TIM Speaker',
    cableType: 'speaker',
    price: usd(4300, 'APPROXIMATE', 2024, 'About EUR 4000 for a terminated 3 m pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    termination: ['banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b8941f',
    tags: ['upper-series', 'tim-family', 'rhodium-connectors'],
    notes:
      'Positioned by the maker as a worked-up version of the ES-TIM, with rhodium-plated speaker connectors in place of the gold ones lower down. Rhodium is harder and more corrosion-resistant than gold and its bulk resistivity is higher; across a bolted spade that difference is a few microhms and is swamped by the contact area. The cable section is what the engine reads.',
    provenanceNote:
      'Series name, position relative to ES-TIM and the rhodium connectors are from the maker\'s published material. Gauge and geometry are inferred. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Absolue Creations',
    model: 'UL-TIM Speaker',
    cableType: 'speaker',
    price: usd(8600, 'APPROXIMATE', 2024, 'About EUR 8000 for a terminated 3 m pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    termination: ['banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a8830f',
    tags: ['flagship-series', 'tim-family'],
    notes:
      'Top series. The maker states the stranded conductors are copper with each individual strand silver-plated to keep the strand surfaces from oxidising -- a real durability argument, since an oxidised strand surface raises the contact resistance between strands over years. At around 10 AWG per leg the loop resistance is near 0.006 ohm per metre, which is about as low as a domestic speaker cable usefully goes.',
    provenanceNote:
      'Series name and the per-strand silver-plated copper construction are from the maker\'s published material. Gauge and geometry are inferred from the class of cable. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Absolue Creations',
    model: 'UL-TIM Interconnect',
    cableType: 'interconnect',
    price: usd(3300, 'CURRENT_RETAIL', 2024, 'About EUR 3060 for a 1 m pair; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Screened twisted pair',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a8830f',
    tags: ['flagship-series', 'tim-family', 'balanced'],
    notes:
      'Flagship-series interconnect, sold with Neutrik XLR bodies on the balanced version. Silver-plated stranded copper in a screened pair; the pair geometry keeps capacitance lower than a coax of the same gauge, which is the only mechanism by which an interconnect can affect the band at all, and at 1 m it is nowhere near enough to.',
    provenanceNote:
      'Series name, retail price, conductor construction and connector brand are from the maker\'s published material. Gauge and geometry are inferred. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Absolue Creations',
    model: 'UL-TIM Power',
    cableType: 'power',
    price: usd(1510, 'CURRENT_RETAIL', 2024, 'About EUR 1400 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'France',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 16,
    shielding: 'Screened',
    termination: ['IEC C13', 'IEC C19', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#a8830f',
    tags: ['flagship-series', 'tim-family'],
    notes:
      'The mains cord of the top series, built from the same silver-plated stranded copper as the signal cables and offered with C13 or high-current C19 ends. The gauge and the C19 option are the meaningful specifications, since a power amplifier can pull tens of amps on transients. The engine applies no signal processing to power cords, and this entry is a good illustration of the rule: careful metalwork, zero DSP.',
    provenanceNote:
      'Series name, retail price and conductor construction are from the maker\'s published material. Gauge, rating and geometry are inferred from the class of cord. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Absolue Creations',
    model: 'TIM Power 2',
    cableType: 'power',
    price: usd(2400, 'APPROXIMATE', 2024, 'About EUR 2200 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'France',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Screened',
    termination: ['IEC C19', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#8a6b0c',
    tags: ['tim-family', 'high-current'],
    notes:
      'Second-generation cord in the TIM Power line, built heavy enough to want a C19 connector. A 10 AWG conductor carries 20 A with a volt drop of a few hundredths of a volt over 1.5 m, which is the honest measure of a mains cord: it is a current-delivery component, upstream of a rectifier and a reservoir bank. No signal processing is applied.',
    provenanceNote:
      'Model name and generation are from the maker\'s published range. Gauge, rating, conductor detail and geometry are inferred from the class of cord. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Absolue Creations',
    model: 'TIM Reference Interconnect',
    cableType: 'interconnect',
    price: usd(6000, 'MSRP', 2023, 'Retail for a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Screened twisted pair',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8a6b0c',
    tags: ['former-flagship', 'tim-family'],
    notes:
      'The former flagship of the range, still sold and still carrying a five-figure ticket for a speaker pair. As an interconnect its modelled contribution is a few tens of picofarads of shunt capacitance across 1 m, the same order as the entry series. The engine reports the computed in-band effect rather than the position in the catalogue.',
    provenanceNote:
      'Range name, position as former flagship and the 1 m RCA retail price are published. Gauge, conductor detail and geometry are inferred. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Absolue Creations',
    model: 'USB-TIM',
    cableType: 'usb',
    price: usd(970, 'APPROXIMATE', 2024, 'About EUR 900 for a 1 m A to B assembly; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil and braid',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#c9a227',
    tags: ['usb-2.0', 'tim-family'],
    notes:
      'The USB assembly in the TIM family, listed in the maker\'s catalogue as a distinct product rather than as a series variant. It is a 90 ohm differential pair with power conductors and a screen, like every compliant USB 2.0 lead. The engine applies no signal processing to USB.',
    provenanceNote:
      'Product name and cable type are from the maker\'s catalogue. Gauge, screening and impedance follow from the USB 2.0 specification rather than a published per-model figure.',
  }),

  // =========================================================================
  // Odeion Cables -- France. Five current ranges named with Greek letters,
  // Omicron through Sigma, each built in every cable type. The maker publishes
  // range descriptions and no construction data at all, so every entry below
  // is an estimate and every provenance note says so.
  // =========================================================================

  makeCable({
    mfr: 'Odeion Cables',
    model: 'Omicron Cable d\'enceintes',
    cableType: 'speaker',
    price: usd(650, 'APPROXIMATE', 2024, 'About EUR 600 for a terminated 3 m pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#00a6a6',
    tags: ['entry-range'],
    notes:
      'The entry speaker cable of the current line, replacing the Alpha range the maker sold from 2010. Odeion publishes no conductor gauge, geometry or electrical data for any of its cables, so the figures the engine uses here come from a copper twisted-pair assumption at a section typical of the class and should be read as exactly that: a class estimate, not a measurement of this cable.',
    provenanceNote:
      'Range name, cable type and position in the line are from the maker\'s published catalogue. Conductor material, gauge and geometry are all inferred from the class of cable; the maker publishes no construction data. Electrical figures are derived from those assumptions.',
  }),

  makeCable({
    mfr: 'Odeion Cables',
    model: 'Gamma Cable d\'enceintes',
    cableType: 'speaker',
    price: usd(1080, 'APPROXIMATE', 2024, 'About EUR 1000 for a terminated 3 m pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#10b3b0',
    tags: ['mid-range'],
    notes:
      'Second rung of the line, described by the maker as a pared-back version of its earlier Omega cable. Assumed here to carry a slightly heavier section than the Omicron, which is the only difference the engine could act on; if the two are in fact the same section then their modelled behaviour is identical, and nothing in this database asserts otherwise.',
    provenanceNote:
      'Range name and position are published. Conductor material, gauge and geometry are inferred from the class of cable; the maker publishes no construction data. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Odeion Cables',
    model: 'Epsilon Cable d\'enceintes',
    cableType: 'speaker',
    price: usd(1950, 'APPROXIMATE', 2024, 'About EUR 1800 for a terminated 3 m pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#0d9b98',
    tags: ['core-range'],
    notes:
      'The maker calls the Epsilon the backbone of the current line and the direct successor to its long-running Omega speaker cable. Modelled as a copper twisted pair at a mid speaker section; over a 3 m run that puts on the order of 0.04 ohm in series with the loudspeaker, and the audible consequence follows from the impedance curve of whatever it is connected to.',
    provenanceNote:
      'Range name, lineage and cable type are published. Conductor material, gauge and geometry are inferred; the maker publishes no construction data. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Odeion Cables',
    model: 'Delta Cable d\'enceintes',
    cableType: 'speaker',
    price: usd(3800, 'APPROXIMATE', 2024, 'About EUR 3500 for a terminated 3 m pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#0a8785',
    tags: ['upper-range'],
    notes:
      'Fourth of the five ranges. Assumed to be a heavier-section version of the same copper construction; at around 11 AWG per leg the derived loop resistance is near 0.008 ohm per metre. Cost within this line climbs far faster than any electrical property the engine can compute from what the maker publishes.',
    provenanceNote:
      'Range name and position are published. Conductor material, gauge and geometry are inferred; the maker publishes no construction data. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Odeion Cables',
    model: 'Sigma Cable d\'enceintes',
    cableType: 'speaker',
    price: usd(7600, 'APPROXIMATE', 2024, 'About EUR 7000 for a terminated 3 m pair; converted.'),
    priceLengthM: 3,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 10,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#066c6a',
    tags: ['flagship-range'],
    notes:
      'Top of the line. Modelled at the heaviest section in the range, which brings loop resistance to roughly 0.006 ohm per metre. Beyond this point extra copper stops mattering: the cable is already far below the amplifier output impedance of most designs, and that impedance, not the cable, is then what sets the damping the driver actually sees.',
    provenanceNote:
      'Range name and position are published. Conductor material, gauge and geometry are inferred; the maker publishes no construction data. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Odeion Cables',
    model: 'Omicron Modulation RCA',
    cableType: 'interconnect',
    price: usd(430, 'APPROXIMATE', 2024, 'About EUR 400 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 23,
    shielding: 'Screened',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#00a6a6',
    tags: ['entry-range', 'single-ended'],
    notes:
      'Entry single-ended interconnect, the replacement for the Alpha lead the maker sold previously. Modelled as an ordinary screened coax at interconnect gauge: a 1 m run presents under 100 pF to the source, which puts the low-pass corner far above the band for any normal output impedance.',
    provenanceNote:
      'Range name, cable type and lineage are published. Conductor material, gauge, screening and geometry are inferred; the maker publishes no construction data. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Odeion Cables',
    model: 'Sigma Modulation RCA',
    cableType: 'interconnect',
    price: usd(5400, 'APPROXIMATE', 2024, 'About EUR 5000 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 21,
    shielding: 'Screened',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#066c6a',
    tags: ['flagship-range', 'single-ended'],
    notes:
      'Flagship single-ended lead. Same format and length as the entry interconnect a dozen times cheaper, and the same handful of picofarads of source loading once the geometry is accounted for. The engine will report the same in-band result for both, because that is what the physics gives.',
    provenanceNote:
      'Range name and cable type are published. Conductor material, gauge, screening and geometry are inferred; the maker publishes no construction data. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Odeion Cables',
    model: 'Epsilon Modulation XLR',
    cableType: 'interconnect',
    price: usd(2400, 'APPROXIMATE', 2024, 'About EUR 2200 for a 1 m balanced pair; converted.'),
    priceLengthM: 1,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Screened twisted pair',
    termination: ['XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#0d9b98',
    tags: ['core-range', 'balanced'],
    notes:
      'Balanced lead from the core range. A screened twisted pair driven differentially rejects common-mode pickup at the receiver, which is a property of the connection format and holds for any competent balanced cable. Derived pair capacitance across 1 m is far below the level at which the audio band notices.',
    provenanceNote:
      'Range name and cable type are published. Conductor material, gauge, screening and geometry are inferred; the maker publishes no construction data. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Odeion Cables',
    model: 'Epsilon Secteur',
    cableType: 'power',
    price: usd(1950, 'APPROXIMATE', 2024, 'About EUR 1800 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    termination: ['IEC C13', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#0d9b98',
    tags: ['core-range'],
    notes:
      'Mains cord in the core range, sold with European 16 A ends. The engine applies no signal processing to power cords: a mains lead sits upstream of the rectifier, regulation and reservoir capacitance of whatever it feeds, and there is no established mechanism by which it changes the audio signal. Selecting it changes the chain description and the system price only.',
    provenanceNote:
      'Range name and cable type are published. Gauge, rating, conductor material and geometry are inferred from the class of cord and the European market it is sold into. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Odeion Cables',
    model: 'Sigma Cable Secteur',
    cableType: 'power',
    price: usd(5900, 'APPROXIMATE', 2024, 'About EUR 5500 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 16,
    shielding: 'Screened',
    termination: ['IEC C13', 'IEC C19', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#066c6a',
    tags: ['flagship-range'],
    notes:
      'The flagship cord of the line, at a price that would buy a good integrated amplifier. It is a heavy screened mains lead with 16 A ends, and the DSP the engine applies to it is identity. That is not a criticism of the cord; it is the honest position of this database on the whole category.',
    provenanceNote:
      'Range name and cable type are published. Gauge, rating, screening, conductor material and geometry are inferred from the class of cord. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Odeion Cables',
    model: 'Epsilon Numerique Ethernet',
    cableType: 'ethernet',
    price: usd(1300, 'APPROXIMATE', 2024, 'About EUR 1200 for a 1.5 m patch lead; converted.'),
    priceLengthM: 1.5,
    country: 'France',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: '100 ohm balanced pairs, RJ45',
    shielding: 'Screened',
    termination: ['RJ45'],
    confidence: 'ESTIMATED',
    accent: '#0d9b98',
    tags: ['shielded', 'core-range'],
    notes:
      'Screened RJ45 lead from the core range. Ethernet is packetised and checksummed, errors trigger retransmission, and a streamer buffers seconds of audio ahead of the converter, so no analog waveform ever crosses this cable. The engine applies no signal processing here. The model name loses its acute accent to the ASCII rule of this database.',
    provenanceNote:
      'Range name and cable type are published. Gauge, screening and pair impedance follow from the Ethernet standard rather than a published per-model figure; the maker publishes no construction data. No DSP is applied to this type.',
  }),

  // =========================================================================
  // Signal Projects -- United Kingdom. Twelve series from Moonstone up to
  // Avaton, grouped by the maker into Reference, Signature and Statement
  // tiers, and offered in every cable type including USB and Ethernet.
  // =========================================================================

  makeCable({
    mfr: 'Signal Projects',
    model: 'Alpha Interconnect',
    cableType: 'interconnect',
    price: usd(390, 'APPROXIMATE', 2024, 'About EUR 360 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Foil and braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#e07a5f',
    tags: ['entry-series', 'single-ended'],
    notes:
      'Bottom of the range above the Moonstone. Screened copper interconnect in single-ended and balanced form; the modelled effect is the shunt capacitance of a 1 m run against the source output impedance, which for any ordinary line stage leaves the audio band flat.',
    provenanceNote:
      'Series name and cable type are from the maker\'s published range. Conductor material, gauge, screening and geometry are inferred from the class of cable. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Signal Projects',
    model: 'Monitor Speaker Cable',
    cableType: 'speaker',
    price: usd(870, 'APPROXIMATE', 2024, 'About EUR 800 for a terminated 3 m pair; converted.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#e07a5f',
    tags: ['entry-series'],
    notes:
      'Second series from the bottom, in speaker form. Stranded copper at around 12 AWG per leg gives roughly 0.01 ohm of loop resistance per metre; over 3 m that is about 0.03 ohm in series with the driver, which the engine divides against the loudspeaker impedance curve frequency by frequency.',
    provenanceNote:
      'Series name and cable type are from the maker\'s published range. Conductor material, gauge and geometry are inferred from the class of cable. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Signal Projects',
    model: 'Lynx Interconnect',
    cableType: 'interconnect',
    price: usd(1030, 'CURRENT_RETAIL', 2024, 'About EUR 950 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'solid-core',
    gauge: 21,
    shielding: 'Foil and braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d4614a',
    tags: ['signature-tier', 'tri-core', 'solid-core'],
    notes:
      'The maker describes a Tri-Core conductor: two solid-core sections and one stranded section, all copper. Mixing solid and stranded in one leg changes the current distribution across the bundle at high frequency very slightly and changes the mechanical behaviour of the cable a lot. Electrically it behaves as a widely spaced conductor pair, which means lower capacitance and higher inductance than a close-laid pair -- the usual trade.',
    provenanceNote:
      'Series name, retail price and the Tri-Core copper construction are from the maker\'s published material. Gauge and screening are inferred. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Signal Projects',
    model: 'Lynx USB Cable',
    cableType: 'usb',
    price: usd(760, 'APPROXIMATE', 2024, 'About EUR 700 for a 1 m A to B assembly; converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil and braid',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#d4614a',
    tags: ['usb-2.0', 'signature-tier'],
    notes:
      'Signal Projects builds a USB assembly in nearly every series; this is the Lynx one. A compliant USB 2.0 lead carries a 90 ohm differential pair and separate bus power, and the receiving DAC either recovers the packets or asks for them again. The engine applies no signal processing to USB.',
    provenanceNote:
      'Series name and cable type are from the maker\'s published range. Gauge, screening and impedance follow from the USB 2.0 specification rather than a published per-model figure.',
  }),

  makeCable({
    mfr: 'Signal Projects',
    model: 'SilverQuest Interconnect',
    cableType: 'interconnect',
    price: usd(1620, 'APPROXIMATE', 2024, 'About EUR 1500 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Foil and braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#c9553d',
    tags: ['signature-tier'],
    notes:
      'Sits above the Lynx in the maker\'s ordering. Silver has about eight percent lower resistivity than copper, so a hybrid conductor lowers series resistance by a few percent at most -- on a line-level interconnect feeding a high-impedance input, series resistance was never the limiting term to begin with.',
    provenanceNote:
      'Series name and its position above the Lynx are from the maker\'s published range. The hybrid conductor is inferred from the series description rather than confirmed per model; gauge, screening and geometry are inferred. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Signal Projects',
    model: 'Hydra Interconnect',
    cableType: 'interconnect',
    price: usd(2200, 'APPROXIMATE', 2024, 'About EUR 2000 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 20,
    shielding: 'Aluminium foil 1 mm thick under a high-purity copper braid',
    dielectric: 'Dual-layer PTFE and polyolefin',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c9553d',
    tags: ['signature-tier', 'published-construction'],
    notes:
      'The most fully documented cable in this group: the maker states 0.84 mm conductors in twisted pairs, a hybrid of six-nines copper with gold and silver, dual-layer PTFE and polyolefin insulation, and a shield of 1 mm aluminium foil under a high-purity copper braid. A 0.84 mm conductor is close to 20 AWG, and that figure plus the twisted-pair spacing is what the engine turns into resistance, inductance and capacitance.',
    provenanceNote:
      'Conductor diameter, hybrid metallurgy, dual-layer dielectric and shield construction are all published by the maker; the gauge here is the stated 0.84 mm diameter converted to AWG. The maker still publishes no resistance, inductance or capacitance, so those are derived.',
  }),

  makeCable({
    mfr: 'Signal Projects',
    model: 'Ultraviolet Power Cable',
    cableType: 'power',
    price: usd(1950, 'APPROXIMATE', 2024, 'About EUR 1800 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 16,
    shielding: 'Foil and braid',
    termination: ['IEC C13', 'IEC C19', 'BS1363', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#b8492f',
    tags: ['shielded', 'high-current'],
    notes:
      'Mains cord from the middle of the range, offered with C13 or C19 outlets and British or European plugs. Heavy conductors and a screened jacket; the current rating is the specification that matters when a power amplifier draws transient peaks. The engine applies no signal processing to power cords.',
    provenanceNote:
      'Series name and cable type are from the maker\'s published range. Gauge, rating, screening and geometry are inferred from the class of cord. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Signal Projects',
    model: 'Apollon Interconnect',
    cableType: 'interconnect',
    price: usd(4000, 'CURRENT_RETAIL', 2024, 'About EUR 3700 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 20,
    shielding: 'Foil and braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#b8492f',
    tags: ['upper-tier'],
    notes:
      'The maker places the Apollon in the middle of its catalogue with four series above it. It is a screened hybrid-conductor pair at roughly 20 AWG, and its modelled contribution over 1 m is a shunt capacitance of a few tens of picofarads against the source, which is the same order as every other interconnect in this file.',
    provenanceNote:
      'Series name, retail price and catalogue position are published. Conductor metallurgy is inferred from the maker\'s description of its upper series; gauge, screening and geometry are inferred. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Signal Projects',
    model: 'Atlantis Speaker Cable',
    cableType: 'speaker',
    price: usd(8100, 'APPROXIMATE', 2024, 'About EUR 7500 for a terminated 3 m pair; converted.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 10,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#a33d26',
    tags: ['statement-tier'],
    notes:
      'First of the Statement series in speaker form. Heavy hybrid conductors at around 10 AWG per leg, giving roughly 0.006 ohm of loop resistance per metre. At that level the cable contributes about a thousandth of a nominal 8 ohm load, and the amplifier output impedance is usually the larger of the two terms.',
    provenanceNote:
      'Series name, tier and cable type are from the maker\'s published range. Conductor metallurgy is inferred from the maker\'s description of its upper series; gauge and geometry are inferred. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Signal Projects',
    model: 'Andromeda Speaker Cable',
    cableType: 'speaker',
    price: usd(13000, 'APPROXIMATE', 2024, 'About EUR 12000 for a terminated 3 m pair; converted.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 9,
    termination: ['banana', 'spade'],
    confidence: 'ESTIMATED',
    accent: '#a33d26',
    tags: ['statement-tier'],
    notes:
      'Statement-series speaker cable, sold single-wire or bi-wire. The bi-wire version splits the run into separate low and high sections, which changes the loop resistance each driver sees and is the one part of the bi-wiring argument that has a mechanism. This entry models the single-wire configuration.',
    provenanceNote:
      'Series name, tier, cable type and the single-wire and bi-wire options are from the maker\'s published range. Conductor metallurgy, gauge and geometry are inferred. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Signal Projects',
    model: 'Golden Sequence Interconnect',
    cableType: 'interconnect',
    price: usd(12900, 'CURRENT_RETAIL', 2024, 'About GBP 10125 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 20,
    shielding: 'Foil and braid',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8c2f1c',
    tags: ['statement-tier', 'flagship'],
    notes:
      'The maker describes this series as built without a cost target. As an interconnect it is still a screened conductor pair carrying line level over one metre, and the engine models what that does: a shunt capacitance of a few tens of picofarads on the source, and a computed in-band response that rounds to zero decibels.',
    provenanceNote:
      'Series name, tier and the 1 m retail price are published. Conductor metallurgy, gauge, screening and geometry are inferred from the maker\'s description of the tier. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Signal Projects',
    model: 'Avaton USB Cable',
    cableType: 'usb',
    price: usd(6500, 'APPROXIMATE', 2024, 'About EUR 6000 for a 1 m A to B assembly; converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil and braid',
    termination: ['USB A to B'],
    confidence: 'ESTIMATED',
    accent: '#8c2f1c',
    tags: ['usb-2.0', 'statement-tier'],
    notes:
      'The USB assembly from the top series in the range, and the clearest case in this file of the rule the engine enforces. A USB 2.0 link is packetised and error-checked, and an asynchronous DAC generates its own conversion clock, so a compliant cable either delivers the data or fails. No signal processing is applied here, at any price.',
    provenanceNote:
      'Series name, tier and cable type are from the maker\'s published range; the price is approximate. Gauge, screening and impedance follow from the USB 2.0 specification rather than a published per-model figure.',
  }),

  // =========================================================================
  // Titan Audio -- Belfast, Northern Ireland. Almost entirely a mains-cable
  // maker, with a small Styx signal range alongside. Nine of the eleven
  // entries below are power cords, which the engine does not process at all.
  // =========================================================================

  makeCable({
    mfr: 'Titan Audio',
    model: 'Styx Mains Cable',
    cableType: 'power',
    price: usd(65, 'CURRENT_RETAIL', 2024, 'About GBP 50 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    currentRatingA: 13,
    shielding: 'Braided screen',
    termination: ['IEC C13', 'BS1363'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8d99ae',
    tags: ['entry-range', 'hand-made'],
    notes:
      'The entry cord, hand-made in Belfast with a three-core OFC construction under a braided screen and a fused British plug. It is the reference point the maker uses for the rest of the range, which is quoted in multiples of its conductor count and fractions of its resistance. The engine applies no signal processing to power cords.',
    provenanceNote:
      'Conductor material, three-core screened construction, hand assembly and retail price are published. Gauge and current rating follow from the 13 A fused British plug it is supplied with. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Titan Audio',
    model: 'Tyco Mains Cable',
    cableType: 'power',
    price: usd(115, 'APPROXIMATE', 2024, 'About GBP 90 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 13,
    shielding: 'Braided screen',
    termination: ['IEC C13', 'BS1363'],
    confidence: 'ESTIMATED',
    accent: '#8d99ae',
    tags: ['entry-range', 'hand-made'],
    notes:
      'Sits between the Styx and the Nyx in the maker\'s ordering. Heavier conductors than the entry cord under the same braided screen. Gauge, screen and connector quality are the whole specification for a mains lead, and the engine leaves the audio untouched when one is selected.',
    provenanceNote:
      'Model name and its position between the Styx and Nyx are published. Gauge, rating, screening and geometry are inferred from the class of cord. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Titan Audio',
    model: 'Nyx Mains Cable',
    cableType: 'power',
    price: usd(165, 'CURRENT_RETAIL', 2024, 'About GBP 130 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 13,
    shielding: 'Braided screen',
    termination: ['IEC C13', 'BS1363'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7a8798',
    tags: ['high-current', 'hand-made'],
    notes:
      'Three-core OFC with a braided shield, which the maker states carries three and a half times the conductor count of the Styx and half its resistance. Halving the resistance of a mains lead halves the volt drop under load, and on a large amplifier that is a real if small number. It is also not a signal-path effect, so the engine applies no processing here.',
    provenanceNote:
      'Three-core OFC construction, braided shield, conductor count relative to the Styx and the halved resistance are all published by the maker. The gauge here follows from that stated resistance ratio. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Titan Audio',
    model: 'Nyx Signature Mains Cable',
    cableType: 'power',
    price: usd(255, 'APPROXIMATE', 2024, 'About GBP 200 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 13,
    shielding: 'Braided screen',
    termination: ['IEC C13', 'BS1363'],
    confidence: 'ESTIMATED',
    accent: '#7a8798',
    tags: ['high-current', 'signature'],
    notes:
      'Signature version of the Nyx, which in this range means upgraded plug and IEC hardware over the same cable. Connector quality is a contact-resistance and retention question and it is a legitimate thing to spend money on. The engine still applies no signal processing to power cords.',
    provenanceNote:
      'Model name and its place in the Signature line are published. Gauge, rating, screening and geometry are inferred from the standard Nyx. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Titan Audio',
    model: 'Helios Mains Cable',
    cableType: 'power',
    price: usd(285, 'CURRENT_RETAIL', 2024, 'About GBP 225 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 16,
    shielding: 'Braided screen',
    termination: ['IEC C13', 'IEC C19', 'BS1363'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#6a7788',
    tags: ['high-current', 'hand-made'],
    notes:
      'Slots between the Nyx and the Eros. Heavier conductor section again, and available with a C19 outlet for equipment that draws enough current to need one. Volt drop over 1.5 m at this gauge is a couple of hundredths of a volt at typical draw. No signal processing is applied.',
    provenanceNote:
      'Model name, catalogue position and retail price are published. Gauge, rating, screening and geometry are inferred from the class of cord. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Titan Audio',
    model: 'Helios Signature Mains Cable',
    cableType: 'power',
    price: usd(415, 'CURRENT_RETAIL', 2024, 'About GBP 325 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 16,
    shielding: 'Braided screen',
    termination: ['IEC C13', 'IEC C19', 'BS1363'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#6a7788',
    tags: ['high-current', 'signature'],
    notes:
      'Signature build of the Helios, with upgraded connector hardware over the same conductor. Titan quotes it at half again the price of the standard version, and the physical difference is at the ends rather than in the middle. The engine applies no signal processing to power cords.',
    provenanceNote:
      'Model name, its Signature designation and the retail price are published. Gauge, rating, screening and geometry are inferred from the standard Helios. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Titan Audio',
    model: 'Eros Mains Cable',
    cableType: 'power',
    price: usd(570, 'CURRENT_RETAIL', 2024, 'About GBP 450 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 16,
    shielding: 'Braided screen',
    termination: ['IEC C13', 'IEC C19', 'BS1363'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5a6778',
    tags: ['flagship', 'occ', 'high-current'],
    notes:
      'Single-crystal copper conductors at the heaviest section Titan builds; the maker hard-wires the same cable into its Eros distribution block. Ohno continuous-cast copper has bulk resistivity within about one percent of ordinary OFC, so the section rather than the casting is what carries the current. The engine applies no signal processing to power cords.',
    provenanceNote:
      'Single-crystal copper conductor, catalogue position and retail price are published. Gauge, rating, screening and geometry are inferred from the class of cord. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Titan Audio',
    model: 'Eros Signature Mains Cable',
    cableType: 'power',
    price: usd(825, 'APPROXIMATE', 2024, 'About GBP 650 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 16,
    shielding: 'Braided screen',
    termination: ['IEC C13', 'IEC C19', 'BS1363'],
    confidence: 'ESTIMATED',
    accent: '#5a6778',
    tags: ['flagship', 'occ', 'signature'],
    notes:
      'Signature build of the flagship cord, with upgraded plug and IEC hardware over the single-crystal copper conductor. This is the most expensive thing Titan sells that plugs into a wall, and the amount of DSP the engine applies to it is none.',
    provenanceNote:
      'Model name and its Signature designation are published; the price is approximate. Gauge, rating, screening and geometry are inferred from the standard Eros. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Titan Audio',
    model: 'Elektra Mains Cable',
    cableType: 'power',
    price: usd(1000, 'APPROXIMATE', 2024, 'About GBP 790 for a 1.5 m cord; converted.'),
    priceLengthM: 1.5,
    country: 'United Kingdom',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 16,
    shielding: 'Braided screen',
    termination: ['IEC C19', 'BS1363'],
    confidence: 'ESTIMATED',
    accent: '#4a5768',
    tags: ['flagship', 'occ', 'high-current'],
    notes:
      'The heaviest cord in the range, launched alongside an extended-length Helios. Built for amplifiers whose transient current draw makes the C19 connector worth having rather than a styling choice. The engine applies no signal processing to power cords.',
    provenanceNote:
      'Model name and its position at the top of the mains range are published; the price is approximate. Gauge, rating, conductor material, screening and geometry are inferred from the class of cord. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Titan Audio',
    model: 'Styx Speaker Cable',
    cableType: 'speaker',
    price: usd(255, 'APPROXIMATE', 2024, 'About GBP 200 for a terminated 3 m pair; converted.'),
    priceLengthM: 3,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 13,
    dielectric: 'Extruded PVC',
    shielding: 'Galvanised steel braid',
    termination: ['banana', 'spade'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8d99ae',
    tags: ['shielded', 'steel-braid'],
    notes:
      'OFC conductors in an extruded PVC dielectric under a galvanised steel braid, offered in 3 m and 5 m and made to order at other lengths. A steel braid is an unusual screen for a speaker cable: steel is ferromagnetic, so it is a better magnetic shield than copper at low frequency and a worse electrical conductor, which is a sensible trade for a screen that carries no signal current. The modelled effect is the loop resistance and inductance of the conductors inside it.',
    provenanceNote:
      'Conductor material, extruded PVC dielectric, galvanised steel braid and the 3 m and 5 m stock lengths are published. Gauge and geometry are inferred. Electrical figures are derived.',
  }),

  makeCable({
    mfr: 'Titan Audio',
    model: 'Styx Interconnect',
    cableType: 'interconnect',
    price: usd(150, 'APPROXIMATE', 2024, 'About GBP 120 for a 1 m stereo pair; converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided screen',
    termination: ['RCA'],
    confidence: 'ESTIMATED',
    accent: '#8d99ae',
    tags: ['entry-range'],
    notes:
      'The analog lead in the Styx signal range, which also includes a digital coaxial cable. Single-ended screened construction; over 1 m the shunt capacitance it presents to a source is small enough that the computed in-band response is flat to well inside a hundredth of a decibel for any normal output stage.',
    provenanceNote:
      'The existence of an interconnect in the Styx signal range and its conductor material are published. Gauge, screening and geometry are inferred from the class of cable. Electrical figures are derived.',
  }),

  // =========================================================================
  // Russ Andrews -- Kendal, United Kingdom. The own-brand cable line is mains
  // cords built on Kimber woven geometry; the interconnects and speaker cables
  // the company sells are branded Kimber and belong to that manufacturer, so
  // they are not duplicated here. Every entry below is therefore a power cord,
  // and the engine applies no signal processing to any of them.
  // =========================================================================

  makeCable({
    mfr: 'Russ Andrews',
    model: 'YellO Power',
    cableType: 'power',
    price: usd(85, 'CURRENT_RETAIL', 2024, 'From about GBP 65 per metre; converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 14,
    currentRatingA: 13,
    termination: ['IEC C13', 'BS1363'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#f2c14e',
    tags: ['woven', 'entry-range', 'reissue'],
    notes:
      'A reissue of the yellow woven cord the company sold through the 1990s, built on Kimber braided geometry rather than a conventional twisted three-core. Weaving the conductors so adjacent runs carry opposing current cancels much of the external field, which lowers loop inductance -- a genuine and measurable property of the geometry. On a mains lead it has no signal-path consequence, and the engine applies no processing.',
    provenanceNote:
      'The woven Kimber geometry, the reissue history and the per-metre price are published. Gauge and current rating follow from the 13 A fused British plug. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Russ Andrews',
    model: 'Classic PowerKord',
    cableType: 'power',
    price: usd(190, 'APPROXIMATE', 2024, 'About GBP 150 for a 1 m cord; converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 13,
    currentRatingA: 13,
    termination: ['IEC C13', 'BS1363'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#f2c14e',
    tags: ['woven', 'long-running'],
    notes:
      'The cord the company built its name on and still supports, now superseded by the Evolution range. Woven OFC conductors with a fused British plug. Construction and price are real; the DSP contribution is zero, as it is for every mains lead in this database.',
    provenanceNote:
      'Model name, woven construction and its supersession by the Evolution range are published; the price is approximate. Gauge and rating follow from the fused British plug. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Russ Andrews',
    model: 'Evolution-100 PowerKord',
    cableType: 'power',
    price: usd(250, 'CURRENT_RETAIL', 2024, 'About GBP 195 for a 1 m cord; from GBP 169 at 0.5 m. Converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 13,
    currentRatingA: 13,
    termination: ['IEC C13', 'BS1363'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e8b33c',
    tags: ['woven', 'evolution-range'],
    notes:
      'Entry point of the current Evolution range. The number in the name tracks the conductor count in the weave, which is the parameter that sets both the effective section and the degree of field cancellation. More conductors means lower series resistance and lower loop inductance. Neither reaches the audio signal, and the engine applies no processing to power cords.',
    provenanceNote:
      'Model name, range structure and retail pricing are published. Gauge and rating follow from the fused British plug the cord is supplied with. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Russ Andrews',
    model: 'Evolution-300 PowerKord',
    cableType: 'power',
    price: usd(510, 'APPROXIMATE', 2024, 'About GBP 400 for a 1 m cord; converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 12,
    currentRatingA: 13,
    termination: ['IEC C13', 'BS1363'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#e8b33c',
    tags: ['woven', 'evolution-range'],
    notes:
      'The middle Evolution cord, and the one the company nominates as the upgrade path for owners of the Classic PowerKord. Heavier aggregate section in the weave than the Evolution-100. The engine applies no signal processing to power cords.',
    provenanceNote:
      'Model name, range structure and the stated upgrade path from the Classic PowerKord are published; the price is approximate. Gauge and rating follow from the fused British plug. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Russ Andrews',
    model: 'Evolution-500 PowerKord',
    cableType: 'power',
    price: usd(890, 'APPROXIMATE', 2024, 'About GBP 700 for a 1 m cord; converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 11,
    currentRatingA: 13,
    termination: ['IEC C13', 'IEC C19', 'BS1363'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d9a52e',
    tags: ['woven', 'evolution-range', 'high-current'],
    notes:
      'Top of the Evolution range, with the highest conductor count in the weave and a C19 option for high-draw equipment. Aggregate section here is heavy enough that the volt drop over a metre is negligible at any domestic load. No signal processing is applied.',
    provenanceNote:
      'Model name and range structure are published; the price is approximate. Gauge, rating and connector options are inferred from the class of cord. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Russ Andrews',
    model: 'Signature PowerKord',
    cableType: 'power',
    price: usd(1300, 'APPROXIMATE', 2024, 'About GBP 1025 for a 1 m cord; converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 11,
    currentRatingA: 13,
    termination: ['IEC C13', 'IEC C19', 'BS1363'],
    confidence: 'ESTIMATED',
    accent: '#d9a52e',
    tags: ['woven', 'signature'],
    notes:
      'Signature-level cord above the Evolution range, on the same woven geometry with upgraded terminations. Connector contact quality is a real reliability specification for a mains lead. It is also not a signal-path effect, and the engine applies none.',
    provenanceNote:
      'Model name and its position above the Evolution range are published; the price is approximate. Gauge, rating, conductor detail and geometry are inferred from the class of cord. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Russ Andrews',
    model: 'SuperKord Reference-SD II',
    cableType: 'power',
    price: usd(2300, 'APPROXIMATE', 2024, 'About GBP 1800 for a 1 m cord; converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 11,
    currentRatingA: 13,
    termination: ['IEC C13', 'IEC C19', 'BS1363'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c4931f',
    tags: ['woven', 'superkord', 'filtered'],
    notes:
      'The SuperKord line adds passive filter networks to the woven cable, and this second-generation Reference version uses the company\'s Sawyers Disc arrangement. A passive network across a mains lead is a real electrical component with a real impedance against radio-frequency energy on the supply; it is upstream of the rectifier, regulator and reservoir bank of whatever it feeds. The engine applies no signal processing to power cords, so this entry is priced and described but not modelled.',
    provenanceNote:
      'Model name, the passive filter network, the Sawyers Disc technology and the second-generation designation are published; the price is approximate. Gauge, rating and geometry are inferred. No DSP is applied to this type.',
  }),

  makeCable({
    mfr: 'Russ Andrews',
    model: 'SuperKord Signature-SD II',
    cableType: 'power',
    price: usd(3800, 'APPROXIMATE', 2024, 'About GBP 3000 for a 1 m cord; converted.'),
    priceLengthM: 1,
    country: 'United Kingdom',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 10,
    currentRatingA: 13,
    termination: ['IEC C13', 'IEC C19', 'BS1363'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b3850f',
    tags: ['woven', 'superkord', 'filtered', 'flagship'],
    notes:
      'The most elaborate mains cord the company builds: the heaviest woven conductor bundle, the same second-generation Sawyers Disc filtering as the Reference, and upgraded terminations. It is a substantial piece of engineering and the engine applies exactly zero DSP to it, which is the correct answer rather than a dismissive one.',
    provenanceNote:
      'Model name, the passive filter network, the Sawyers Disc technology and the second-generation designation are published; the price is approximate. Gauge, rating and geometry are inferred. No DSP is applied to this type.',
  }),
];
